import { createSlice } from '@reduxjs/toolkit'

import {
  completePayment,
  fetchDeal,
  fetchCustomer,
  fetchToken,
  calculateTax,
  createCardToken,
} from './actions'

const initialState = {
  error: false,
  errorEmail: null,
  loading: false,
  deal: null,
  token: null,
  tokenError: null,
  tokenIsLoading: false,
  exemptCertificateId: null,
  completion: 'idle',
  salesTaxCalculation: 'idle',
  cardToken: null,
  cardTokenCreationStatus: 'idle',
  paymentFormComplete: false,
  verificationActive: false,
  countryCode: undefined,
  postalCode: undefined,
  postalCodeError: false,
  estimate: null,
  taxlessEstimate: null
}

const paymentLinkFormSlice = createSlice({
  name: 'paymentLinkForm',
  initialState,
  reducers: {
    updatePlan: (state, action) => {
      state.deal.plan = action.payload
    },
    setPurchaseError: (state, action) => {
      state.deal.error = action.payload
    },
    failPaymentVerificationAfterRedeem: (state, action) => {
      state.completion = 'idle'
      state.deal.error = action.payload
    },
    setPaymentFormComplete: (state, action) => {
      state.paymentFormComplete = action.payload
    },
    setVerificationActive: (state, action) => {
      state.verificationActive = action.payload
    },
    resetCardToken: (state) => {
      state.cardToken = null

      const { plan } = state.deal
      if (!plan) return

      plan.tax_amount = null
      plan.total_with_tax = null
      plan.tax_exempt = null

      state.deal.totals.tax_amount = null
      state.deal.totals.payment_due_today = null
    },
    resetTokenError: (state) => {
      state.tokenError = null
    },
    setCountryCode: (state, { payload }) => {
      state.countryCode = payload
    },
    setPostalCode: (state, { payload }) => {
      state.postalCode = payload
    },
    setPostalCodeError: (state, { payload }) => {
      state.postalCodeError = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDeal.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchDeal.fulfilled, (state, action) => {
      state.loading = false
      state.error = false
      state.errorEmail = null

      const deal = action.payload
      const {
        totals: {
          total_price: totalPrice,
          discount_text: discountText,
          payment_due_today: paymentDueToday,
          discount_amount: discountAmount,
          coupon_type: couponType,
          tiers
        }
      } = deal

      state.deal = deal

      const addOnsTotal = deal.addons
        ? state.deal.addons.reduce((sum, addon) => sum + addon.total, 0)
        : 0

      state.deal.totals = {
        payment_due_today_without_tax: totalPrice + addOnsTotal - discountAmount,
        discount_text: discountText,
        payment_due_today: paymentDueToday,
        discount_amount: discountAmount,
        coupon_type: couponType,
        tiers
      }

      state.deal.plan.payment_due_today = paymentDueToday
      state.deal.plan.payment_without_discount = totalPrice
      state.deal.plan.discount_amount = discountAmount
      state.deal.plan.discount_text = discountText
    })
    builder.addCase(fetchDeal.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload.message
      state.errorEmail = action.payload.account_email
      state.deal = null
    })
    builder.addCase(fetchCustomer.fulfilled, (state, action) => {
      state.externalCustomerId = action.payload
    })
    builder.addCase(fetchCustomer.rejected, (state, action) => {
      state.tokenError = action.payload
    })
    builder.addCase(fetchToken.fulfilled, (state, action) => {
      state.tokenIsLoading = false
      state.token = action.payload
    })
    builder.addCase(fetchToken.rejected, (state, action) => {
      state.tokenIsLoading = false
      state.tokenError = action.payload
    })
    builder.addCase(fetchToken.pending, (state, _action) => {
      state.tokenIsLoading = true
    })
    builder.addCase(completePayment.pending, (state, _action) => {
      state.completion = 'pending'
    })
    builder.addCase(completePayment.rejected, (state, action) => {
      state.completion = 'idle'
      state.deal.error = action.payload
    })
    builder.addCase(completePayment.fulfilled, (state, _action) => {
      state.completion = 'done'
    })
    builder.addCase(calculateTax.pending, (state, _action) => {
      state.salesTaxCalculation = 'pending'
      state.deal.error = false
    })
    builder.addCase(calculateTax.rejected, (state, action) => {
      state.salesTaxCalculation = 'idle'
      state.deal.error = action.payload
      state.deal.totals.payment_due_today = null
      state.deal.totals.tax_amount = null
    })
    builder.addCase(calculateTax.fulfilled, (state, action) => {
      state.salesTaxCalculation = 'idle'
      const estimate = action.payload[0].resource

      state.estimate = estimate
      state.taxlessEstimate = action.payload[1]?.resource

      state.deal.plan.tax_amount = estimate.tax_amount
      state.deal.plan.total_with_tax = estimate.total

      state.deal.totals.tax_amount = estimate.tax_amount
      state.deal.totals.payment_due_today = estimate.total
      state.deal.totals.discount_amount = estimate.discount
    })
    builder.addCase(createCardToken.pending, (state, _action) => {
      state.cardTokenCreationStatus = 'pending'
    })
    builder.addCase(createCardToken.fulfilled, (state, action) => {
      state.cardTokenCreationStatus = 'idle'
      state.cardToken = action.payload

      state.countryCode = action.payload.card?.country

      if (action.payload.card?.address_zip) {
        state.postalCode = action.payload.card?.address_zip
      }
    })
    builder.addCase(createCardToken.rejected, (state, action) => {
      state.cardTokenCreationStatus = 'idle'
      state.cardToken = null
      state.deal.error = action.payload
    })
  }
})

export const {
  resetTokenError,
  setPurchaseError,
  failPaymentVerificationAfterRedeem,
  resetCardToken,
  setPaymentFormComplete,
  setVerificationActive,
  setCountryCode,
  setPostalCode,
  setPostalCodeError
} = paymentLinkFormSlice.actions

export const { reducer } = paymentLinkFormSlice
